import { combineReducers } from 'redux';

const masterInitialState = {
  list: null,
  loadStatus: 0,
  didLoadAll: false,
  query: {
    tab: 'all',
    keyword: '',
    size: 20,
  },
};

const detailInitialState = {
  loadStatus: 0,
  saveStatus: 0,
  payType: null,
};

const createInitialState = {
  orderId: null,
  orderType: 0,
  extra: null,
  tips: {},
  key: null,
  addressId: null,
  warehouseId: null,
  selectedList: [],
  warehouseError: null,
  expectedCashCoupon: null,
  expectedCashList: [],
  postageList: [],
  postageId: null,
  /* -1: 没库存, 0: 未提交 , 1: 提交中 , 2: 创建成功  */
  saveStatus: 0,
  preCreateStatus: 0,
};

const trackDetailInitialState = {
  loadStatus: 0,
  data: null,
};

function masterReducer(state = masterInitialState, action) {
  switch (action.type) {
    case 'LOAD_HAPPYGO_ORDER_LIST': {
      if (!action.willReload) {
        return {
          ...state,
          loadStatus: 1,
        };
      }

      return {
        ...state,
        loadStatus: 1,
        query: {
          ...state.query,
          ...action.query,
        },
        list: null,
      };
    }

    case 'LOAD_HAPPYGO_ORDER_LIST_COMPLETION': {
      if (action.error) {
        return {
          ...state,
          loadStatus: 0,
        };
      }

      const list = action.list.map(order => order.orderId);

      return {
        ...state,
        loadStatus: 2,
        didLoadAll: list.length < state.query.size,
        list: state.list ? state.list.concat(list) : list,
      };
    }

    default:
      return state;
  }
}

function ordersReducer(state = {}, action) {
  switch (action.type) {
    case 'LOAD_HAPPYGO_ORDER_COMPLETION': {
      const { orderId, data, error } = action;

      if (error) {
        return state;
      }

      return {
        ...state,
        [orderId]: data,
      };
    }

    case 'LOAD_HAPPYGO_ORDER_LIST_COMPLETION': {
      if (action.error) {
        return state;
      }

      const orders = action.list.reduce(
        (obj, order) => {
          const orderId = order.orderId;

          if (orderId) {
            obj[orderId] = order;
          }
          return obj;
        },
        { ...state }
      );

      return orders;
    }

    case 'REFUND_HAPPYGO_ORDER_COMPLETION':
    case 'CANCEL_HAPPYGO_ORDER_COMPLETION': {
      if (action.error) {
        return state;
      }

      const order = action.data;
      const orderId = order.orderId;
      const orders = { ...state };

      if (orderId) {
        orders[orderId] = { ...orders[orderId], ...order };
      }

      return orders;
    }

    default:
      return state;
  }
}

function detailsReducer(state = {}, action) {
  switch (action.type) {
    case 'LOAD_HAPPYGO_ORDER': {
      const { orderId } = action;

      return {
        ...state,
        [orderId]: {
          ...(state[orderId] || detailInitialState),
          loadStatus: 1,
        },
      };
    }

    case 'LOAD_HAPPYGO_ORDER_COMPLETION': {
      const { orderId, error } = action;

      return {
        ...state,
        [orderId]: {
          ...(state[orderId] || detailInitialState),
          loadStatus: error ? 0 : 2,
        },
      };
    }

    case 'REFUND_HAPPYGO_ORDER':
    case 'PAY_HAPPYGO_ORDER_BY_WECHAT':
    case 'CANCEL_HAPPYGO_ORDER': {
      const { orderId } = action;

      return {
        ...state,
        [orderId]: {
          ...(state[orderId] || detailInitialState),
          saveStatus: 1,
        },
      };
    }

    case 'REFUND_HAPPYGO_ORDER_COMPLETION':
    case 'PAY_HAPPYGO_ORDER_BY_WECHAT_COMPLETION':
    case 'CANCEL_HAPPYGO_ORDER_COMPLETION': {
      const { orderId, error } = action;

      return {
        ...state,
        [orderId]: {
          ...(state[orderId] || detailInitialState),
          saveStatus: error ? 0 : 2,
        },
      };
    }

    default:
      return state;
  }
}

function createReducer(state = createInitialState, action) {
  switch (action.type) {
    case 'RESET_HAPPYGO_ORDER_INFO': {
      return createInitialState;
    }

    case 'PRE_CREATE_HAPPYGO_ORDER': {
      return {
        ...state,
        preCreateStatus: 1,
      };
    }

    case 'PRE_CREATE_HAPPYGO_ORDER_COMPLETION': {
      const { error, data, orderType, selectedList } = action;

      if (error) {
        return {
          ...state,
          preCreateStatus: 0,
        };
      }

      let res = {};
      let defaultPostageId, defaultAddressId;

      data.order && (res.extra = data.order);
      if (data.coupon) {
        const { postageCouponList, postageCouponId } = data.coupon;
        defaultPostageId = postageCouponId ? postageCouponId : null;
        res.postageList = (postageCouponList || []).map(coupon => coupon.code);
      }
      data.order.address && (defaultAddressId = data.order.address.aid);

      return {
        ...state,
        ...res,
        addressId: state.addressId ? state.addressId : defaultAddressId,
        postageId: defaultPostageId,
        orderId: null,
        orderType,
        selectedList,
        warehouseError: null,
        warehouseId: null,
        preCreateStatus: 2,
      };
    }

    case 'CREATE_HAPPYGO_ORDER': {
      return {
        ...state,
        saveStatus: 1,
      };
    }

    case 'CREATE_HAPPYGO_ORDER_COMPLETION': {
      const { error, data } = action;

      if (error) {
        if (error.code === -401) {
          const warehouseList = data.warehouse;
          const products = (data.products || []).reduce((obj, productId) => {
            obj[productId] = true;
            return obj;
          }, {});

          return {
            ...state,
            saveStatus: -1,
            warehouseId: null,
            warehouseError: {
              message: error.message,
              products,
              warehouseList,
            },
          };
        }

        return {
          ...state,
          saveStatus: 0,
        };
      }
      return {
        ...state,
        saveStatus: 2,
        orderId: data.orderId,
      };
    }

    case 'PICK_ORDER_POSTAGE': {
      return {
        ...state,
        postageId: action.postageId || null,
      };
    }

    case 'PICK_ORDER_ADDRESS': {
      return {
        ...state,
        addressId: action.address.aid || null,
      };
    }
    case 'UPDATE_ORDER_WAREHOUSE': {
      return {
        ...state,
        warehouseId: action.warehouseId || null,
      };
    }

    default:
      return state;
  }
}

function trackDetailsReducer(state = {}, action) {
  switch (action.type) {
    case 'LOAD_HAPPYGO_ORDER_TRACK': {
      const { expressNo } = action;

      return {
        ...state,
        [expressNo]: {
          ...(state[expressNo] || trackDetailInitialState),
          loadStatus: 1,
        },
      };
    }

    case 'LOAD_HAPPYGO_ORDER_TRACK_COMPLETION': {
      const { expressNo, data, error } = action;

      if (error) {
        return state;
      }

      return {
        ...state,
        [expressNo]: {
          ...(state[expressNo] || trackDetailInitialState),
          loadStatus: 2,
          data,
        },
      };
    }

    default:
      return state;
  }
}

export default combineReducers({
  orders: ordersReducer,
  master: masterReducer,
  details: detailsReducer,
  create: createReducer,
  trackDetails: trackDetailsReducer,
});
